/**
 * Catalogue figures on a pipeline Loan row.
 *
 * The Deal Inspector and the grid read `loan.propertyValue`, `loan.debtYield`,
 * `loan.noi`, `loan.capitalStack[0].spread` and `loan.cashFlows.revenue` —
 * top-level keys GET /loans never sends. Those figures live in the row's
 * `custom_fields` under their catalogue names, which the camel-casing
 * transform turns into `propertyValue`, `debtYield`, `fullyFundedAmount`
 * and so on. So the Inspector printed "—" for a value, debt yield, spread,
 * fees, revenue and NOI the deal holds, under a banner blaming the record,
 * and the "debt yield below 8%" exception could never fire.
 *
 * These readers take the top-level field when a row carries one and the
 * catalogue value otherwise, and put each on the scale the pipeline uses
 * (ratios are fractions, as `pct()` expects).
 */
import type { Loan } from '@/types'
import {
  SENIOR_NOT_STATED,
  bpsLabel,
  rateText,
  seniorAheadUnstated,
  spreadLabel,
} from '@/components/deal/dossier/dealTerms'
import { cityFromLocation, geoFromLocation } from '@/utils/geo'
import { COUPON_MIN, MIN_COUPON_BASIS, couponDisplay } from './couponDisplay'
import { deskDateIso } from './deskTime'

function camel(name: string): string {
  return name.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
}

function stated(value: unknown): unknown {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'string' && value.trim() === '') return undefined
  return value
}

/**
 * A catalogue value from the row's custom fields, under either spelling.
 *
 * GET /loans camel-cases the bag (`term_months` arrives as `termMonths`), and a
 * dossier edit that could not reach the server patches the row in memory under
 * the catalogue's own snake_case key (`applySnakePatchToLoan`) — leaving the
 * stale camel key beside the fresh one. The snake_case key is the only one a
 * person's edit ever writes, so where both are present it is the newer: reading
 * the camel key first kept showing the figure the edit had just replaced.
 */
export function customValue(loan: Pick<Loan, 'customFields'>, name: string): unknown {
  const fields = loan.customFields ?? {}
  return stated(fields[name]) ?? stated(fields[camel(name)])
}

function finite(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[$,%\s]/g, ''))
  return Number.isFinite(n) ? n : null
}

function positive(value: unknown): number | null {
  const n = finite(value)
  return n != null && n > 0 ? n : null
}

const getter = (loan: Pick<Loan, 'customFields'>) => (name: string) => customValue(loan, name)

/** A tranche behind senior debt the record does not state (see dealTerms). */
export function subordinateWithoutSenior(loan: Pick<Loan, 'customFields' | 'capitalStack'>): boolean {
  return seniorAheadUnstated(getter(loan), loan.capitalStack)
}

/**
 * Debt yield as a fraction (0.0769 for 7.69%).
 *
 * The catalogue stores it in percentage points (7.6855); a row that carries a
 * top-level value carries a fraction. Null for a subordinate note whose senior
 * debt is not stated: NOI over the note alone is not its debt yield.
 */
export function loanDebtYield(loan: Pick<Loan, 'debtYield' | 'customFields' | 'capitalStack'>): number | null {
  if (subordinateWithoutSenior(loan)) return null
  const topLevel = positive(loan.debtYield)
  if (topLevel != null) return topLevel > 1.5 ? topLevel / 100 : topLevel
  // custom_fields percents are always percentage points (the parser's pct100
  // and the derivation rules write 0-100). Guessing the scale from the
  // magnitude read a 1.2% in-place debt yield as 120% and kept a deal with
  // almost no income clear of the "below 8%" exception.
  const points = positive(customValue(loan, 'debt_yield'))
  return points == null ? null : points / 100
}

/**
 * A debt-yield fraction to two decimals (0.076854 → "7.69%"), as the dossier
 * and the committee strip print it. The Inspector used one decimal: 7.69%
 * read 7.7%, and a 7.95% deal read "8.0%" beside "below program minimum".
 */
export function debtYieldPct(fraction: number): string {
  return `${(fraction * 100).toFixed(2)}%`
}

/** The debt-yield cell as the Inspector and grid print it. */
export function debtYieldText(loan: Pick<Loan, 'debtYield' | 'customFields' | 'capitalStack'>, format: (n: number) => string): string {
  if (subordinateWithoutSenior(loan)) return SENIOR_NOT_STATED
  const dy = loanDebtYield(loan)
  return dy == null ? '—' : format(dy)
}

export function loanPropertyValue(loan: Pick<Loan, 'propertyValue' | 'customFields'>): number | null {
  return positive(loan.propertyValue) ?? positive(customValue(loan, 'property_value'))
}

export function loanNoi(loan: Pick<Loan, 'noi' | 'customFields'>): number | null {
  return positive(loan.noi) ?? positive(customValue(loan, 'noi'))
}

export function loanGrossRevenue(loan: Pick<Loan, 'cashFlows' | 'customFields'>): number | null {
  return positive(customValue(loan, 'gross_revenue')) ?? positive(loan.cashFlows?.revenue)
}

/**
 * The total commitment (initial + future advances) from the catalogue.
 *
 * The column read only the disbursed `funded_amount`, which is empty on every
 * deal that has not closed — The Ardsley holds a $62M fully funded amount and
 * showed "—". A closed deal with no catalogue commitment still shows what was
 * funded, as before.
 */
export function loanFullyFunded(loan: Pick<Loan, 'customFields' | 'fundedAmount'>): number | null {
  return positive(customValue(loan, 'fully_funded_amount')) ?? positive(loan.fundedAmount)
}

const PRICING_NUMBER = '[+-]?(?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?'
const PRICING_INDEX = '(?:(?:CME\\s+)?(?:Term\\s+)?SOFR|LIBOR|Prime|S|L)'
const SPREAD_UNIT = '(?:bps?|basis\\s*points?)'
const PRICING_UNIT = '(?:' + SPREAD_UNIT + '|%)'
const RANGE_NEXT = '\\s*(?:[-–—/]|to\\b|or\\b)\\s*(?:' + PRICING_INDEX + '\\s*\\+\\s*)?' + PRICING_NUMBER
const AND_NEXT = '\\s*and\\b\\s*(?:'
  + PRICING_INDEX + '\\s*\\+\\s*' + PRICING_NUMBER + '(?:\\s*' + PRICING_UNIT + ')?'
  + '|' + PRICING_NUMBER + '\\s*' + SPREAD_UNIT + '\\b'
  + '|' + PRICING_NUMBER + '(?![\\d.]|\\s*%))'
const NEXT_PRICING_AMOUNT = '(?:' + RANGE_NEXT + '|' + AND_NEXT + ')'
const COMPOSITE_PRICING = new RegExp(
  '\\b' + PRICING_INDEX + '\\s*\\+\\s*' + PRICING_NUMBER + '(?:\\s*' + PRICING_UNIT + ')?' + NEXT_PRICING_AMOUNT
  + '|' + PRICING_NUMBER + '\\s*' + PRICING_UNIT + NEXT_PRICING_AMOUNT
  + '|' + PRICING_NUMBER + RANGE_NEXT + '\\s*' + SPREAD_UNIT + '\\b'
  + '|' + PRICING_NUMBER + AND_NEXT,
  'i',
)

/** Convert stated basis points for the pipeline without changing the record.
 * Keep the index and any annotations: SOFR + 375 bps is SOFR + 3.75%, not an
 * all-in coupon. Bare indexed spreads of 20 or more follow the established
 * basis-point shorthand (S+285); other unlabelled prose stays as recorded. */
export function pipelinePercentText(text: string): string {
  // A range or multiple pricing legs is one quote. Keep it whole rather than
  // converting only one amount and displaying mixed units. "and 1% floor" is
  // an annotation, not a second spread.
  if (COMPOSITE_PRICING.test(text)) return text
  const percent = (bps: string) => `${bps.startsWith('+') ? '+' : ''}${Number((Number(bps.replace(/,/g, '')) / 100).toFixed(10))}%`
  const explicit = text.replace(
    /(^|[^\w.])([+-]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)\s*(?:bps?|basis\s*points?)\b/gi,
    (_, before: string, bps: string) => `${before}${percent(bps)}`,
  )
  return explicit.replace(
    /\b((?:(?:CME\s+)?(?:Term\s+)?SOFR|LIBOR|Prime|S|L)\s*\+\s*)(\d+(?:\.\d+)?)(?![\d.%])\b(?!\s*(?:%|bps?\b|basis\s*points?\b))/gi,
    (match, index: string, bps: string) => Number(bps) >= 20 ? `${index}${percent(bps)}` : match,
  )
}

/** A spread in percent, using the dossier's established scales for bare numbers. */
export function loanSpread(loan: Pick<Loan, 'customFields' | 'capitalStack'>): string | null {
  const stated = customValue(loan, 'spread')
  if (stated !== undefined) {
    const label = spreadLabel(stated)
    return label === null ? null : pipelinePercentText(label)
  }
  const layer = loan.capitalStack?.[0]?.spread
  const label = layer != null && Number.isFinite(layer) ? spreadLabel(layer) : null
  return label === null ? null : pipelinePercentText(label)
}

/** Origination and exit fees in basis points, from the catalogue. */
export function loanFees(loan: Pick<Loan, 'customFields'>): string | null {
  const origination = bpsLabel(customValue(loan, 'origination_fee_bps') ?? customValue(loan, 'origination_fee'))
  const exit = bpsLabel(customValue(loan, 'exit_fee_bps') ?? customValue(loan, 'exit_fee'))
  const parts = [origination ? `${origination} in` : null, exit ? `${exit} out` : null].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/* ---------------------------------------------------------------------------
 * Backshop columns (Report Studio, Columns panel, saved views, exports).
 *
 * Each reads the same catalogue value the Deal Inspector and the dossier read —
 * never a second opinion — and each has a server twin in
 * `backend/app/reporting/view_grid.py`, held to the same cells by
 * `tests/fixtures/view_grid_parity.json`. A change here is a change there.
 * ------------------------------------------------------------------------ */

/** dealTerms ``present``: a value is absent when blank, and "—" is the dash a
 *  screen prints for absent, never a stored value. */
function termPresent(value: unknown): boolean {
  if (value === null || value === undefined) return false
  const text = String(value).trim()
  return text !== '' && text !== '—'
}

/** A catalogue value as the text a reader prints, or null when it is absent. */
export function termText(value: unknown): string | null {
  return termPresent(value) ? String(value).trim() : null
}

/** dealTerms ``finite``: a number, or numeric text with commas and spaces. */
function termNumber(value: unknown): number | undefined {
  if (!termPresent(value)) return undefined
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[,\s]/g, ''))
  return Number.isFinite(n) ? n : undefined
}

/** The first of `names` the record states, under the catalogue name first and
 *  the legacy key only as a fallback (dealTerms ``first``). */
function firstTerm(loan: Pick<Loan, 'customFields'>, names: string[]): unknown {
  for (const name of names) {
    const value = customValue(loan, name)
    if (termPresent(value)) return value
  }
  return undefined
}

/** A catalogue percent held in points (65 is 65%), as the ratio the grid
 *  formats. Never guessed from its magnitude: 1.2 is 1.2%, not 120%. */
function pointsAsRatio(loan: Pick<Loan, 'customFields'>, name: string): number | null {
  const points = positive(customValue(loan, name))
  return points == null ? null : points / 100
}

/** Loan-to-cost as a fraction. */
export function loanLtc(loan: Pick<Loan, 'customFields'>): number | null {
  return pointsAsRatio(loan, 'ltc')
}

/** A deal book's "In Place LTV/LTC" as a fraction: one figure the sheet does
 *  not say is the LTV or the LTC, so it is never read as either. */
export function loanInPlaceLtvLtc(loan: Pick<Loan, 'customFields'>): number | null {
  return pointsAsRatio(loan, 'in_place_ltv_ltc')
}

/** The stabilized LTV as a fraction. Never the in-place LTV: a stabilized
 *  figure the record does not state is not stated by the as-is one. */
export function loanLtvStabilized(loan: Pick<Loan, 'customFields'>): number | null {
  return pointsAsRatio(loan, 'ltv_stabilized')
}

/** The stabilized debt yield as a fraction. Withheld, like the in-place one,
 *  for a subordinate note whose senior debt is not stated. Never the
 *  in-place figure. */
export function loanDebtYieldStabilized(
  loan: Pick<Loan, 'customFields' | 'capitalStack'>,
): number | null {
  return subordinateWithoutSenior(loan) ? null : pointsAsRatio(loan, 'debt_yield_stabilized')
}

/** The stabilized-debt-yield cell as the grid and the exports print it. */
export function debtYieldStabilizedText(
  loan: Pick<Loan, 'customFields' | 'capitalStack'>,
  format: (n: number) => string,
): string {
  if (subordinateWithoutSenior(loan)) return SENIOR_NOT_STATED
  const dy = loanDebtYieldStabilized(loan)
  return dy == null ? '—' : format(dy)
}

/**
 * The number of units, rooms or beds the deal records — never a square-foot
 * area ("212 units" is a count; an office's 310,000 SF under a Units header is
 * not). A demo row sends `units` at the top level; a live row only in the
 * catalogue.
 */
export function loanUnits(loan: Pick<Loan, 'customFields' | 'units'> & { unitCount?: number | null }): number | null {
  const native = termNumber(loan.unitCount)
  if (native !== undefined && native > 0) return native
  const count = termNumber(customValue(loan, 'units') ?? loan.units)
  if (count === undefined || count <= 0) return null
  const measure = (termText(customValue(loan, 'unit_of_measure')) ?? 'Units').toLowerCase()
  // `sf` and `sm` state an AREA, not a count (sm is never converted to sf).
  return measure === 'sf' || measure === 'sm' ? null : count
}

/** What `loanUnits` counts: "Units", "Rooms", "Beds". */
export function loanUnitMeasure(loan: Pick<Loan, 'customFields'>): string {
  return termText(customValue(loan, 'unit_of_measure')) ?? 'Units'
}

/**
 * The deal's area in square feet.
 *
 * Backshop's Units column carries the deal's PRIMARY count whatever it counts —
 * an office's 713,251 SF sits under "Units" — while `loanUnits` is a count of
 * units, rooms or beds only, as the dossier's Units / Keys row is. This is the
 * other half: the catalogue's `sf`, or, where the deal's primary unit of
 * measure is SF, the primary count itself (the Pipeline Report reads it the
 * same way, `builder._units_cell`).
 */
export function loanSquareFeet(
  loan: Pick<Loan, 'customFields' | 'units' | 'sf'> & { squareFeet?: number | null },
): number | null {
  const native = termNumber(loan.squareFeet)
  if (native !== undefined && native > 0) return native
  const area = termNumber(customValue(loan, 'sf') ?? loan.sf)
  if (area !== undefined && area > 0) return area
  const measure = (termText(customValue(loan, 'unit_of_measure')) ?? 'Units').toLowerCase()
  if (measure !== 'sf') return null
  const primary = termNumber(customValue(loan, 'units') ?? loan.units)
  return primary !== undefined && primary > 0 ? primary : null
}

/**
 * Backshop's Units cell: the deal's PRIMARY count with the measure it is counted
 * in — "312 Units", "180 Rooms", "713,251 SF". A count of units, rooms, beds or
 * pads leads; the area answers where the deal counts in square feet. Null where
 * neither is stated, so the cell reads "—" and never "0 Units"
 * (`view_grid._units_reading` is the server twin).
 */
export function loanUnitsReading(
  loan: Pick<Loan, 'customFields' | 'units' | 'sf'>,
): string | null {
  const count = loanUnits(loan)
  if (count !== null) return `${count.toLocaleString('en-US')} ${loanUnitMeasure(loan)}`
  const area = loanSquareFeet(loan)
  return area === null ? null : `${area.toLocaleString('en-US')} SF`
}

/** A month count without a unit suffix, and the unchanged number behind it. */
export type MonthsReading = { text: string | null; num: number | null }

const MONTH_SUFFIX = /^([+-]?\d+(?:\.\d+)?)\s*(?:mo(?:s|nths?)?)$/i

/** The count a month-column suffix names. Not a numeric accessor: text
 *  forms stay numberless for sort and sum, as `36 mo` always has. */
function suffixedMonthCount(raw: unknown): number | undefined {
  const match = String(raw).trim().match(MONTH_SUFFIX)
  if (!match) return undefined
  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * A month count with the number kept. Prose that already names its
 * unit ("3 years") passes through as the dossier prints it and has no number
 * to sort or sum.
 */
function monthsReading(raw: unknown, positiveOnly = false): MonthsReading {
  if (!termPresent(raw)) return { text: null, num: null }
  const n = termNumber(raw) ?? suffixedMonthCount(raw)
  if (n === undefined) return { text: String(raw).trim(), num: null }
  // No amortization months is interest-only, which the Amortization type
  // column states; a "0 mo" amortization term is not a term.
  if (positiveOnly && n <= 0) return { text: null, num: null }
  return { text: String(n), num: termNumber(raw) ?? null }
}

export function loanTermMonths(loan: Pick<Loan, 'customFields'>): MonthsReading {
  return monthsReading(firstTerm(loan, ['term_months', 'term']))
}

/** Months of ONE extension option. The number of options is not stored. */
export function loanExtensionMonths(loan: Pick<Loan, 'customFields'>): MonthsReading {
  return monthsReading(firstTerm(loan, ['extensions', 'extension_options']))
}

export function loanIoMonths(loan: Pick<Loan, 'customFields'>): MonthsReading {
  return monthsReading(firstTerm(loan, ['io_months', 'io_period', 'interest_only_months']))
}

export function loanAmortMonths(loan: Pick<Loan, 'customFields'>): MonthsReading {
  return monthsReading(customValue(loan, 'amortization_months'), true)
}

/**
 * Backshop's Amort Term: the amortization in months, or "IO" for an
 * interest-only loan — it has no term, but it is not a loan whose term is
 * unknown, and a dash would say so (`view_grid._amort_reading` is the server twin).
 */
export function loanAmortReading(loan: Pick<Loan, 'customFields'>): string | null {
  const months = loanAmortMonths(loan)
  if (months.text !== null) return months.text
  const kind = (loanAmortizationType(loan) ?? '').toUpperCase()
  if (kind.startsWith('IO')) return 'IO'
  // A stated 0-month term with no type: interest-only. Suffixed zeros
  // ("0mo", "0 months") are the same fact as a stored 0.
  const raw = customValue(loan, 'amortization_months')
  return kind === '' && (termNumber(raw) ?? suffixedMonthCount(raw)) === 0 ? 'IO' : null
}

/** The amortization type as stated ("IO - Interest Only", "Amortizing"). */
export function loanAmortizationType(loan: Pick<Loan, 'customFields'>): string | null {
  return termText(customValue(loan, 'amortization_type')) ?? termText(customValue(loan, 'amortization'))
}

export function loanRateType(loan: Pick<Loan, 'customFields'>): string | null {
  return termText(customValue(loan, 'rate_type'))
}

export function loanPurpose(loan: Pick<Loan, 'customFields'>): string | null {
  return termText(customValue(loan, 'purpose'))
}

function spreadBpsFromNumber(n: number): number {
  return Math.abs(n) < 0.2 ? n * 10_000 : Math.abs(n) < 20 ? n * 100 : n
}

function spreadAsPoints(value: unknown): number | null {
  if (!termPresent(value)) return null
  const n = termNumber(value)
  if (n !== undefined) return spreadBpsFromNumber(n) / 100
  const text = String(value).trim()
  const bps = text.match(/([+-]?(?:\d+(?:\.\d+)?))\s*(?:bps?|basis\s*points?)\b/i)
  if (bps) return Number(bps[1]) / 100
  const pct = text.match(/([+-]?(?:\d+(?:\.\d+)?))\s*%/)
  if (pct) return Number(pct[1])
  const indexed = text.match(
    /(?:(?:CME\s+)?(?:Term\s+)?SOFR|LIBOR|Prime|S|L)\s*\+\s*([+-]?(?:\d+(?:\.\d+)?))/i,
  )
  if (indexed) {
    const amount = Number(indexed[1])
    return (amount >= 20 ? amount : amount * 100) / 100
  }
  return null
}

function floorAsPoints(loan: Pick<Loan, 'customFields'>): number | null {
  const raw = customValue(loan, 'floor')
  const n = termNumber(raw)
  if (n !== undefined) return n
  const text = termText(raw)
  if (text === null) return null
  const pct = text.match(/([+-]?(?:\d+(?:\.\d+)?))\s*%/)
  return pct ? Number(pct[1]) : null
}

function spreadPointsForCoupon(loan: Pick<Loan, 'customFields' | 'capitalStack'>): number | null {
  const stated = customValue(loan, 'spread')
  if (stated !== undefined) return spreadAsPoints(stated)
  return spreadAsPoints(loan.capitalStack?.[0]?.spread)
}

function minCoupon(loan: Pick<Loan, 'customFields' | 'capitalStack'>): string | null {
  const floor = floorAsPoints(loan)
  const spread = spreadPointsForCoupon(loan)
  if (floor === null || spread === null) return null
  const total = floor + spread
  return `${total.toFixed(total % 1 === 0 ? 0 : 2)}%`
}

function statedCoupon(loan: Pick<Loan, 'rate' | 'customFields'>): string | null {
  const stated = termText(loan.rate) ?? termText(customValue(loan, 'rate'))
  const label = stated === null ? null : rateText(stated)
  return label === null ? null : pipelinePercentText(label)
}

/** Pipeline Coupon: stated rate by default; floor + spread under `min_coupon`. */
export function loanCoupon(
  loan: Pick<Loan, 'rate' | 'customFields' | 'capitalStack'>,
): string | null {
  return couponDisplay() === COUPON_MIN ? minCoupon(loan) : statedCoupon(loan)
}

/** The calc basis when Coupon is the minimum coupon, else null. */
export function loanCouponBasis(
  loan: Pick<Loan, 'rate' | 'customFields' | 'capitalStack'>,
): string | null {
  return couponDisplay() === COUPON_MIN && minCoupon(loan) !== null ? MIN_COUPON_BASIS : null
}

/** The brokerage FIRM, distinct from the individual broker. */
export function loanBrokerage(loan: Pick<Loan, 'customFields'>): string | null {
  return termText(customValue(loan, 'brokerage_company'))
}

/** The broker contact: the catalogue's own field, else the broker on the deal
 *  (the Pipeline Report's rule, `builder._dp_row`). */
export function loanBrokerContact(loan: Pick<Loan, 'customFields' | 'brokerName'>): string | null {
  return termText(customValue(loan, 'broker_contact')) ?? termText(loan.brokerName)
}

/** The city: the catalogue's, else the one the location text names. */
export function loanCity(loan: Pick<Loan, 'customFields' | 'location'>): string | null {
  return termText(customValue(loan, 'city')) ?? (cityFromLocation(loan.location) || null)
}

/** A deal book's text for one Pipeline cell, and the reading the record gave the cell at import. */
export type BookCell = { text: string; read: string }

/**
 * The deal book's text for a Pipeline column the record reads otherwise (Units
 * with no measure, State 83 filed as "Multiple locations"), while the record
 * still reads `live` as it did when the deal was imported; null once anything
 * changes it (view_grid.py `BOOK_CELLS_FIELD`, `book_cell_entry`). A Backshop
 * 0 prints as the book states it: every populated cell is in Helios (Edward,
 * 31 Oct 2026, for the Backshop to Helios transition). Stored
 * under snake_case keys, which GET /loans camel-cases back to the column id.
 */
export function bookCellEntry(loan: Pick<Loan, 'customFields'>, column: string, live: string): BookCell | null {
  const cells = customValue(loan, 'deal_book_cells')
  if (!cells || typeof cells !== 'object' || Array.isArray(cells)) return null
  const bag = cells as Record<string, unknown>
  const entry = bag[column] ?? bag[column.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)]
  if (!entry || typeof entry !== 'object') return null
  const { text, read } = entry as Record<string, unknown>
  if (read !== live || typeof text !== 'string' || text.trim() === '') return null
  return { text, read }
}

/** The catalogue's Country, as stated ("United States", "United Kingdom"). */
export function loanCountry(loan: Pick<Loan, 'customFields'>): string | null {
  return termText(customValue(loan, 'country'))
}

/**
 * The state: the catalogue's `state` when the record states one, else the one
 * the location text resolves to. A stated two-letter code is the code; a
 * stated name resolves to its code; text that is neither is shown as stated
 * rather than dropped.
 */
export function loanState(loan: Pick<Loan, 'customFields' | 'location'>): string | null {
  const stated = termText(customValue(loan, 'state'))
  if (stated) {
    return /^[A-Za-z]{2}$/.test(stated) ? stated.toUpperCase() : (geoFromLocation(stated)?.code ?? stated)
  }
  return geoFromLocation(loan.location)?.code ?? null
}

/** The deal's Date Received as written (YYYY-MM-DD) — a calendar day, never
 *  shifted through a time zone. Not the day the record was created. */
export function loanReceived(loan: Pick<Loan, 'originated'>): string | null {
  const raw = (loan.originated ?? '').trim()
  if (!raw) return null
  return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw
}

type KillRecord = Pick<Loan, 'killedAt' | 'deadReason' | 'killReasonCode' | 'killedAtStage'> & {
  /** GET /loans serves the kill timestamp as `dead_at`. */
  deadAt?: string | null
}

/** When the deal was killed, as the desk's calendar day. Null for a live deal:
 *  reopening a deal clears the whole kill record. */
export function loanKilledAt(loan: KillRecord): string | null {
  const when = loan.killedAt ?? loan.deadAt
  return when ? deskDateIso(when) : null
}

/** Why it was killed: the label the kill recorded, else the reason code made
 *  readable ("does_not_fit_program" → "Does not fit program"). */
export function loanKillReason(loan: KillRecord): string | null {
  const label = termText(loan.deadReason)
  if (label) return label
  const code = termText(loan.killReasonCode)
  if (!code) return null
  const words = code.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : null
}

export function loanKilledAtStage(loan: KillRecord): string | null {
  return termText(loan.killedAtStage)
}
